/*
What a contact's access is made of, shared by the places that edit or preset it
(XOL-602/603/604; moved out of ContactDetail.vue's data()).

  CONTACT_ROLES        the six user roles, in the order the Transfer shows them
  normaliseOrgCategory the org row's `category` as the CONTACT sees it
  isTowerCoOrg         is that org a Tower Company
  hasFinanceRole       does a pipe-delimited userRole include `finance` (XOL-674)
  canHaveFinanceAlerts may this contact have the Finance Alerts flag (XOL-674)
  canInviteToOrg       may THIS viewer invite by e-mail into THIS org (D17/D19, R2.7)

The ORG row's `category` and the CONTACT's `orgCategory` are not the same string.
Organisation.category carries a Xolas-Client marker (Xolas Sdn Bhd is literally
'Tower Company Xolas-Client'), which the API strips in Contacts::getOrgCategoryAttribute()
via Organisation::removeXolasClientCategory(). Comparing the raw org value to
'Tower Company' is false for exactly the orgs these features are for, so every comparison
goes through normaliseOrgCategory().

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

export var CONTACT_ROLES = [
  { key: 'project', label: 'Project', value: 'project', type: 'primary' },
  { key: 'property', label: 'Property', value: 'property', type: 'secondary' },
  { key: 'maintenance', label: 'Maintenance', value: 'maintenance', type: 'warning' },
  { key: 'finance', label: 'Finance', value: 'finance', type: 'success' },
  { key: 'admin', label: 'Admin', value: 'admin', type: 'info' },
  { key: 'management', label: 'Management', value: 'management', type: 'primary' }
]

// A fresh copy per caller: el-transfer and the tags read these objects, and one component
// must never be able to change another's list.
export function contactRoles () {
  return CONTACT_ROLES.map(function (role) {
    return Object.assign({}, role)
  })
}

/**
 * Mirrors the server's normalisation: drops the Xolas-Client marker and the comma that
 * separates it.
 *
 * @param {*} raw an org row's `category`
 * @returns {string|null} 'Tower Company', 'Telco', ...; null for nothing usable
 */
export function normaliseOrgCategory (raw) {
  if (typeof raw !== 'string' || !raw) {
    return null
  }

  return raw.replace('Xolas-Client', '').replace(',', '').trim()
}

export function isTowerCoOrg (raw) {
  return normaliseOrgCategory(raw) === 'Tower Company'
}

// XOL-674: mirrors FinanceRecipients::forTowerCompany() in xolas-tms-api:
// `userRole LIKE '%finance%'`. A substring test, not a split on '|', and
// case-insensitive, because MySQL LIKE on contacts.userRole is.
export function hasFinanceRole (userRole) {
  return typeof userRole === 'string' && userRole.toLowerCase().indexOf('finance') !== -1
}

// XOL-674: the API reads sendFinanceAlerts only for a Tower Company contact
// with a finance role, so the switch exists only then. orgCategory may be a
// raw org `category` (with a Xolas-Client marker) or a contact's orgCategory
// (already stripped). normaliseOrgCategory handles both.
export function canHaveFinanceAlerts (orgCategory, userRole) {
  return isTowerCoOrg(orgCategory) && hasFinanceRole(userRole)
}

/**
 * Invite by e-mail (D17 / D19 / R2.7): the button and the Invitations panel share this one rule.
 *
 *   - a Tower Co viewer who is not View Only,
 *   - on a Tower Company org's Contacts tab (D19: any other org, for everyone, has neither),
 *     compared through normaliseOrgCategory, never the raw category,
 *   - and either a super (the `super` flag; isSuper() also passes a legacy `userRole: 'super'`),
 *     or ANY manager (the `manager` flag, whatever the role) on their OWN org, which needs a real
 *     organisation_id on both sides: Number(undefined) is NaN and NaN !== NaN, but 0 === 0.
 *
 * Pure, so the caller passes what it read through the access mixin:
 *   { profile, isTowerCo, isViewOnly, isManager, orgId, orgCategory }
 */
export function canInviteToOrg (ctx) {
  var c = ctx || {}
  var profile = c.profile || {}
  var mine = Number(profile.organisation_id)
  var sameOrg = !!profile.organisation_id && mine > 0 && Number(c.orgId) === mine

  return !!c.isTowerCo && !c.isViewOnly && isTowerCoOrg(c.orgCategory) &&
    (!!profile.super || (!!profile.manager && !!c.isManager && sameOrg))
}
